import {
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

import { TruncateTooltipComponent } from './truncate-tooltip.component';

const TOOLTIP_POSITIONS: ConnectedPosition[] = [
  { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
  { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
];
const TOOLTIP_VIEWPORT_MARGIN = 8;

/**
 * Shows a tooltip with the given text while the pointer is over the host.
 * Same look as rnplTruncateTooltip; rendered in an overlay, so it is not clipped by scroll containers or dialogs.
 *
 * <button [rnplTooltip]="'BUTTON.EDIT' | translate">…</button>
 */
@Directive({
  selector: '[rnplTooltip]'
})
export class TooltipDirective implements OnInit, OnChanges, OnDestroy {

  @Input('rnplTooltip') text: string;

  private overlayRef: OverlayRef = null;
  private componentRef: ComponentRef<TruncateTooltipComponent> = null;
  private unlisteners: (() => void)[] = [];
  private readonly onScroll = () => this.hide();

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly overlay: Overlay,
    private readonly ngZone: NgZone,
    private readonly renderer: Renderer2,
    private readonly injector: Injector,
    private readonly componentFactoryResolver: ComponentFactoryResolver,
  ) {}

  ngOnInit(): void {
    const host = this.elementRef.nativeElement;

    this.ngZone.runOutsideAngular(() => {
      this.unlisteners = [
        this.renderer.listen(host, 'mouseenter', () => this.show()),
        this.renderer.listen(host, 'mouseleave', () => this.hide()),
        this.renderer.listen(host, 'mousedown', () => this.hide()),
      ];
      document.addEventListener('scroll', this.onScroll, true);
    });
  }

  ngOnChanges(): void {
    if (!this.componentRef) { return; }

    if (this.text) {
      this.componentRef.instance.text = this.text;
      this.componentRef.changeDetectorRef.detectChanges();
    } else {
      this.hide();
    }
  }

  ngOnDestroy(): void {
    this.unlisteners.forEach(unlisten => unlisten());
    document.removeEventListener('scroll', this.onScroll, true);
    this.hide();
  }

  private show(): void {
    if (this.overlayRef || !this.text) { return; }

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions(TOOLTIP_POSITIONS)
      .withFlexibleDimensions(false)
      .withViewportMargin(TOOLTIP_VIEWPORT_MARGIN)
      .withPush(true);

    this.ngZone.run(() => {
      this.overlayRef = this.overlay.create({ positionStrategy });

      const portal = new ComponentPortal(TruncateTooltipComponent, null, this.injector, this.componentFactoryResolver);
      this.componentRef = this.overlayRef.attach(portal);
      this.componentRef.instance.text = this.text;
      this.componentRef.changeDetectorRef.detectChanges();
    });
  }

  private hide(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.componentRef = null;
  }
}
